import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Calendar, 
  ChevronLeft, 
  ChevronRight, 
  CheckCircle, 
  XCircle,
  Clock,
  Save,
  Settings,
  Zap,
  Copy,
  CalendarDays,
  Sun,
  Repeat,
  MapPin,
  Sparkles,
  MessageSquare,
  Globe
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { 
  colors, 
  shadows, 
  borders, 
  typography, 
  spacing 
} from '@/components/business/design-system/businessDesignTokens';

interface AvailabilityData {
  availabilityType: 'date_based' | 'always_available' | 'by_request';
  availableDates?: {
    date: string;
    isAvailable: boolean;
    maxBookings?: number;
    currentBookings: number;
    notes?: string;
  }[];
  defaultSettings?: {
    advanceBookingDays: number;
    maxBookingsPerDay: number;
    blockedWeekdays?: number[];
  };
}

interface EnhancedAvailabilityCalendarProps {
  adventureId: string;
  adventureName: string;
  onAvailabilityUpdate?: (availability: AvailabilityData) => void;
}

// Quick templates for tour operators
const QUICK_TEMPLATES = [
  {
    id: 'weekends',
    name: 'Weekends Only',
    description: 'Saturdays and Sundays',
    icon: <Sun className="h-4 w-4" />,
    pattern: [0, 6] // Sunday and Saturday
  },
  {
    id: 'weekdays',
    name: 'Weekdays Only',
    description: 'Monday to Friday',
    icon: <CalendarDays className="h-4 w-4" />,
    pattern: [1, 2, 3, 4, 5]
  },
  {
    id: 'summer',
    name: 'Summer Season',
    description: 'June to August',
    icon: <Sun className="h-4 w-4" />,
    months: [5, 6, 7] // June, July, August (0-indexed)
  },
  {
    id: 'winter',
    name: 'Winter Season',
    description: 'December to February',
    icon: <Sparkles className="h-4 w-4" />,
    months: [11, 0, 1] // December, January, February
  }
];

export function EnhancedAvailabilityCalendar({ 
  adventureId, 
  adventureName, 
  onAvailabilityUpdate 
}: EnhancedAvailabilityCalendarProps) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [availability, setAvailability] = useState<AvailabilityData>({
    availabilityType: 'always_available',
    availableDates: [],
    defaultSettings: {
      advanceBookingDays: 365,
      maxBookingsPerDay: 5,
      blockedWeekdays: []
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectionMode, setSelectionMode] = useState<'single' | 'range' | 'bulk'>('single');
  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const [bulkSettings, setBulkSettings] = useState({
    isAvailable: true,
    maxBookings: 5,
    notes: ''
  });
  const [currentStep, setCurrentStep] = useState<'settings' | 'configuration'>('settings');

  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();

  // Load availability data
  useEffect(() => {
    loadAvailability();
  }, [adventureId]);

  const loadAvailability = async () => {
    try {
      setLoading(true);
      // TODO: Replace with actual API call
      setAvailability({
        availabilityType: 'always_available',
        availableDates: [],
        defaultSettings: {
          advanceBookingDays: 365,
          maxBookingsPerDay: 5,
          blockedWeekdays: []
        }
      });
    } catch (error) {
      console.error('Error loading availability:', error);
      toast.error('Failed to load availability settings');
    } finally {
      setLoading(false);
    }
  };

  const saveAvailability = async () => {
    try {
      setSaving(true);
      // TODO: Replace with actual API call
      toast.success('Availability settings saved successfully');
      onAvailabilityUpdate?.(availability);
    } catch (error) {
      console.error('Error saving availability:', error);
      toast.error('Failed to save availability settings');
    } finally {
      setSaving(false);
    }
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    const currentDate = new Date(startDate);

    for (let i = 0; i < 42; i++) { // 6 weeks * 7 days
      const dateStr = currentDate.toISOString().split('T')[0];
      const isCurrentMonth = currentDate.getMonth() === currentMonth;
      const isToday = currentDate.toDateString() === new Date().toDateString();
      const isPast = currentDate < new Date();
      
      // Find availability for this date
      const dateAvailability = availability.availableDates?.find(d => d.date === dateStr);
      const isSelected = selectedDates.includes(dateStr);

      days.push({
        date: new Date(currentDate),
        dateStr,
        day: currentDate.getDate(),
        isCurrentMonth,
        isToday,
        isPast,
        availability: dateAvailability,
        isSelected
      });

      currentDate.setDate(currentDate.getDate() + 1);
    }

    return days;
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(newDate.getMonth() - 1);
      } else {
        newDate.setMonth(newDate.getMonth() + 1);
      }
      return newDate;
    });
    setSelectedDates([]); // Clear selection when changing months
  };

  const handleDateClick = (dateStr: string, isPast: boolean) => {
    if (isPast) return;

    if (selectionMode === 'single') {
      setSelectedDates([dateStr]);
    } else if (selectionMode === 'bulk') {
      setSelectedDates(prev => 
        prev.includes(dateStr) 
          ? prev.filter(d => d !== dateStr)
          : [...prev, dateStr]
      );
    }
  };

  const applyBulkSettings = () => {
    if (selectedDates.length === 0) {
      toast.error('Please select dates first');
      return;
    }

    const updatedDates = [...(availability.availableDates || [])];
    
    selectedDates.forEach(dateStr => {
      const existingIndex = updatedDates.findIndex(d => d.date === dateStr);
      const dateEntry = {
        date: dateStr,
        isAvailable: bulkSettings.isAvailable,
        maxBookings: bulkSettings.maxBookings,
        currentBookings: 0,
        notes: bulkSettings.notes
      };

      if (existingIndex >= 0) {
        updatedDates[existingIndex] = dateEntry;
      } else {
        updatedDates.push(dateEntry);
      }
    });

    setAvailability(prev => ({
      ...prev,
      availabilityType: 'date_based',
      availableDates: updatedDates
    }));

    setSelectedDates([]);
    toast.success(`Updated ${selectedDates.length} dates`);
  };

  const applyQuickTemplate = (template: typeof QUICK_TEMPLATES[0]) => {
    const updatedDates = [...(availability.availableDates || [])];
    const startDate = new Date(currentYear, currentMonth, 1);
    const endDate = new Date(currentYear, currentMonth + 1, 0);
    
    let datesToUpdate: string[] = [];

    if (template.pattern) {
      // Apply weekday pattern
      for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
        if (template.pattern.includes(d.getDay()) && d >= new Date()) {
          datesToUpdate.push(d.toISOString().split('T')[0]);
        }
      }
    } else if (template.months) {
      // Apply seasonal pattern (for current month if it matches)
      if (template.months.includes(currentMonth)) {
        for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
          if (d >= new Date()) {
            datesToUpdate.push(d.toISOString().split('T')[0]);
          }
        }
      }
    }

    datesToUpdate.forEach(dateStr => {
      const existingIndex = updatedDates.findIndex(d => d.date === dateStr);
      const dateEntry = {
        date: dateStr,
        isAvailable: true,
        maxBookings: 5,
        currentBookings: 0,
        notes: `Applied ${template.name} template`
      };

      if (existingIndex >= 0) {
        updatedDates[existingIndex] = dateEntry;
      } else {
        updatedDates.push(dateEntry);
      }
    });

    setAvailability(prev => ({
      ...prev,
      availabilityType: 'date_based',
      availableDates: updatedDates
    }));

    toast.success(`Applied ${template.name} to ${datesToUpdate.length} dates`);
  };

  const getDateStatus = (day: any) => {
    if (day.isPast) return 'past';
    if (availability.availabilityType === 'always_available') return 'available';
    if (availability.availabilityType === 'by_request') return 'by_request';
    
    if (day.availability?.isAvailable) return 'available';
    if (day.availability?.isAvailable === false) return 'unavailable';
    return 'unknown';
  };

  const getDateStyles = (day: any) => {
    const status = getDateStatus(day);
    const baseStyles = "p-2 text-center text-sm transition-all relative hover:bg-slate-50 border-r border-b cursor-pointer";
    
    if (day.isSelected) {
      return cn(baseStyles, "bg-gradient-to-r from-action-500 to-action-600 text-white hover:from-action-600 hover:to-action-700 font-medium");
    }
    
    if (!day.isCurrentMonth) {
      return cn(baseStyles, "text-slate-300 bg-slate-50 cursor-not-allowed");
    }
    
    switch (status) {
      case 'past':
        return cn(baseStyles, "text-slate-400 cursor-not-allowed");
      case 'available':
        return cn(baseStyles, "hover:bg-green-50 text-slate-900 border-green-200");
      case 'unavailable':
        return cn(baseStyles, "bg-red-50 text-red-700 border-red-200");
      case 'by_request':
        return cn(baseStyles, "bg-amber-50 text-amber-700 border-amber-200");
      default:
        return cn(baseStyles, "text-slate-500");
    }
  };

  const calendarDays = generateCalendarDays();
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Availability mode selection component
  const AvailabilityModeSelector = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <h3 className={cn(typography.h3, `text-${colors.neutral[900]}`)}>
          Choose Availability Mode
        </h3>
        <p className={cn(typography.body, `text-${colors.neutral[600]}`)}>
          Select how customers can book your adventure
        </p>
      </div>

      {/* Mode Selection Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Always Available */}
        <motion.div
          whileHover={{ scale: 1.02 }}
          className={cn(
            "cursor-pointer transition-all duration-200",
            availability.availabilityType === 'always_available' 
              ? `ring-2 ring-${colors.success.main} shadow-lg` 
              : 'hover:shadow-md'
          )}
          onClick={() => setAvailability(prev => ({ ...prev, availabilityType: 'always_available' }))}
        >
          <Card className={cn(
            borders.container,
            shadows.level1.className,
            availability.availabilityType === 'always_available' 
              ? `bg-${colors.success.bg} border-${colors.success.border}` 
              : 'bg-white'
          )}>
            <CardContent className={spacing.cardPadding}>
              <div className="text-center space-y-4">
                <div className={cn(
                  "w-16 h-16 rounded-xl mx-auto flex items-center justify-center",
                  `bg-gradient-to-br from-${colors.success.main} to-${colors.success.light}`,
                  shadows.colored.emerald
                )}>
                  <CheckCircle className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h4 className={cn(typography.h4, `text-${colors.neutral[900]}`)}>
                    Always Available
                  </h4>
                  <p className={cn(typography.body, `text-${colors.neutral[600]}`, "mt-1")}>
                    Adventure can be booked any day
                  </p>
                </div>
                <div className="space-y-2 text-left">
                  <div className="flex items-center gap-2">
                    <CheckCircle className={cn("w-4 h-4", `text-${colors.success.main}`)} />
                    <span className={cn(typography.caption, `text-${colors.neutral[700]}`)}>
                      Set default max bookings
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className={cn("w-4 h-4", `text-${colors.success.main}`)} />
                    <span className={cn(typography.caption, `text-${colors.neutral[700]}`)}>
                      Configure advance booking
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className={cn("w-4 h-4", `text-${colors.success.main}`)} />
                    <span className={cn(typography.caption, `text-${colors.neutral[700]}`)}>
                      Block specific weekdays
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Specific Dates */}
        <motion.div
          whileHover={{ scale: 1.02 }}
          className={cn(
            "cursor-pointer transition-all duration-200",
            availability.availabilityType === 'date_based' 
              ? `ring-2 ring-${colors.primary.main} shadow-lg` 
              : 'hover:shadow-md'
          )}
          onClick={() => setAvailability(prev => ({ ...prev, availabilityType: 'date_based' }))}
        >
          <Card className={cn(
            borders.container,
            shadows.level1.className,
            availability.availabilityType === 'date_based' 
              ? `bg-${colors.primary.bg} border-${colors.primary.border}` 
              : 'bg-white'
          )}>
            <CardContent className={spacing.cardPadding}>
              <div className="text-center space-y-4">
                <div className={cn(
                  "w-16 h-16 rounded-xl mx-auto flex items-center justify-center",
                  `bg-gradient-to-br from-${colors.primary.main} to-${colors.primary.light}`,
                  shadows.colored.blue
                )}>
                  <Calendar className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h4 className={cn(typography.h4, `text-${colors.neutral[900]}`)}>
                    Specific Dates
                  </h4>
                  <p className={cn(typography.body, `text-${colors.neutral[600]}`, "mt-1")}>
                    Set availability for individual dates
                  </p>
                </div>
                <div className="space-y-2 text-left">
                  <div className="flex items-center gap-2">
                    <Calendar className={cn("w-4 h-4", `text-${colors.primary.main}`)} />
                    <span className={cn(typography.caption, `text-${colors.neutral[700]}`)}>
                      Interactive calendar view
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Zap className={cn("w-4 h-4", `text-${colors.primary.main}`)} />
                    <span className={cn(typography.caption, `text-${colors.neutral[700]}`)}>
                      Quick templates available
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Settings className={cn("w-4 h-4", `text-${colors.primary.main}`)} />
                    <span className={cn(typography.caption, `text-${colors.neutral[700]}`)}>
                      Custom booking limits
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* By Request Only */}
        <motion.div
          whileHover={{ scale: 1.02 }}
          className={cn(
            "cursor-pointer transition-all duration-200",
            availability.availabilityType === 'by_request' 
              ? `ring-2 ring-${colors.warning.main} shadow-lg` 
              : 'hover:shadow-md'
          )}
          onClick={() => setAvailability(prev => ({ ...prev, availabilityType: 'by_request' }))}
        >
          <Card className={cn(
            borders.container,
            shadows.level1.className,
            availability.availabilityType === 'by_request' 
              ? `bg-${colors.warning.bg} border-${colors.warning.border}` 
              : 'bg-white'
          )}>
            <CardContent className={spacing.cardPadding}>
              <div className="text-center space-y-4">
                <div className={cn(
                  "w-16 h-16 rounded-xl mx-auto flex items-center justify-center",
                  `bg-gradient-to-br from-${colors.warning.main} to-${colors.warning.light}`,
                  shadows.colored.amber
                )}>
                  <MessageSquare className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h4 className={cn(typography.h4, `text-${colors.neutral[900]}`)}>
                    By Request Only
                  </h4>
                  <p className={cn(typography.body, `text-${colors.neutral[600]}`, "mt-1")}>
                    Customers must request availability
                  </p>
                </div>
                <div className="space-y-2 text-left">
                  <div className="flex items-center gap-2">
                    <MessageSquare className={cn("w-4 h-4", `text-${colors.warning.main}`)} />
                    <span className={cn(typography.caption, `text-${colors.neutral[700]}`)}>
                      Manual approval process
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Globe className={cn("w-4 h-4", `text-${colors.warning.main}`)} />
                    <span className={cn(typography.caption, `text-${colors.neutral[700]}`)}>
                      Custom booking workflow
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className={cn("w-4 h-4", `text-${colors.warning.main}`)} />
                    <span className={cn(typography.caption, `text-${colors.neutral[700]}`)}>
                      Flexible scheduling
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Continue Button */}
      <div className="flex justify-center pt-6">
        <Button
          onClick={() => setCurrentStep('configuration')}
          disabled={!availability.availabilityType}
          className={cn(
            `bg-${colors.primary.main} hover:bg-${colors.primary.hover}`,
            shadows.simple.md,
            "px-8 py-3 text-lg font-semibold"
          )}
        >
          Configure {availability.availabilityType === 'always_available' && 'Default Settings'}
          {availability.availabilityType === 'date_based' && 'Calendar'}
          {availability.availabilityType === 'by_request' && 'Request Workflow'}
          <ChevronRight className="w-5 h-5 ml-2" />
        </Button>
      </div>
    </div>
  );

  // Configuration interface based on selected mode
  const ConfigurationInterface = () => {
    if (availability.availabilityType === 'always_available') {
      return (
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentStep('settings')}
                className="flex items-center gap-2"
              >
                <ChevronLeft className="w-4 h-4" />
                Back to Mode Selection
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={cn(`bg-${colors.success.bg} text-${colors.success.text} border-${colors.success.border}`)}>
                Always Available
              </Badge>
            </div>
          </div>

          {/* Default Settings */}
          <Card className={cn(borders.container, shadows.level1.className)}>
            <CardHeader className={cn(`bg-${colors.neutral[50]}/80 border-b border-${colors.neutral[200]}/50`)}>
              <CardTitle className={cn(typography.h4, `text-${colors.neutral[900]}`)}>
                Default Settings
              </CardTitle>
            </CardHeader>
            <CardContent className={spacing.cardPadding}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className={cn(typography.body, "font-medium", `text-${colors.neutral[700]}`)}>
                    Max Bookings Per Day
                  </Label>
                  <Input
                    type="number"
                    value={availability.defaultSettings?.maxBookingsPerDay || 5}
                    onChange={(e) => setAvailability(prev => ({
                      ...prev,
                      defaultSettings: {
                        ...prev.defaultSettings!,
                        maxBookingsPerDay: parseInt(e.target.value) || 5
                      }
                    }))}
                    className="mt-2"
                    min="1"
                  />
                  <p className={cn(typography.caption, `text-${colors.neutral[600]}`, "mt-1")}>
                    Maximum number of bookings allowed per day
                  </p>
                </div>
                <div>
                  <Label className={cn(typography.body, "font-medium", `text-${colors.neutral[700]}`)}>
                    Advance Booking Days
                  </Label>
                  <Input
                    type="number"
                    value={availability.defaultSettings?.advanceBookingDays || 365}
                    onChange={(e) => setAvailability(prev => ({
                      ...prev,
                      defaultSettings: {
                        ...prev.defaultSettings!,
                        advanceBookingDays: parseInt(e.target.value) || 365
                      }
                    }))}
                    className="mt-2"
                    min="1"
                  />
                  <p className={cn(typography.caption, `text-${colors.neutral[600]}`, "mt-1")}>
                    How far in advance customers can book
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    if (availability.availabilityType === 'date_based') {
      return (
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentStep('settings')}
                className="flex items-center gap-2"
              >
                <ChevronLeft className="w-4 h-4" />
                Back to Mode Selection
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={cn(`bg-${colors.primary.bg} text-${colors.primary.text} border-${colors.primary.border}`)}>
                Specific Dates
              </Badge>
            </div>
          </div>

          {/* Calendar Interface */}
          <Tabs defaultValue="calendar" className="space-y-6">
            <TabsList className={cn(
              `bg-${colors.neutral[50]}/80 p-1 rounded-lg border border-${colors.neutral[200]}/60 shadow-sm h-12`
            )}>
              <TabsTrigger 
                value="calendar" 
                className={cn(
                  "flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-sm transition-all duration-200 px-4 py-2 rounded-md font-medium text-sm h-10"
                )}
              >
                <Calendar className="w-4 h-4" />
                Calendar View
              </TabsTrigger>
              <TabsTrigger 
                value="templates" 
                className={cn(
                  "flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-sm transition-all duration-200 px-4 py-2 rounded-md font-medium text-sm h-10"
                )}
              >
                <Zap className="w-4 h-4" />
                Quick Templates
              </TabsTrigger>
            </TabsList>

            <TabsContent value="calendar" className="space-y-6">
              {/* Calendar Controls */}
              <Card className={cn(borders.container, shadows.level1.className)}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigateMonth('prev')}
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <span className="text-lg font-semibold min-w-[140px] text-center">
                          {monthNames[currentMonth]} {currentYear}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigateMonth('next')}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>

                      <div className="flex items-center gap-2">
                        <Label className="text-sm">Selection Mode:</Label>
                        <Select value={selectionMode} onValueChange={(value: 'single' | 'range' | 'bulk') => setSelectionMode(value)}>
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="single">Single</SelectItem>
                            <SelectItem value="bulk">Bulk Select</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {selectedDates.length > 0 && (
                      <Badge className="bg-blue-100 text-blue-800 border border-blue-200">
                        {selectedDates.length} dates selected
                      </Badge>
                    )}
                  </div>

                  {/* Bulk Settings */}
                  {selectionMode === 'bulk' && (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Label className="text-sm">Available:</Label>
                        <Select 
                          value={bulkSettings.isAvailable ? 'yes' : 'no'} 
                          onValueChange={(value) => setBulkSettings(prev => ({ ...prev, isAvailable: value === 'yes' }))}
                        >
                          <SelectTrigger className="w-20">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="yes">Yes</SelectItem>
                            <SelectItem value="no">No</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-center gap-2">
                        <Label className="text-sm">Max:</Label>
                        <Input
                          type="number"
                          value={bulkSettings.maxBookings}
                          onChange={(e) => setBulkSettings(prev => ({ ...prev, maxBookings: parseInt(e.target.value) || 1 }))}
                          className="w-16"
                          min="1"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          placeholder="Notes (optional)"
                          value={bulkSettings.notes}
                          onChange={(e) => setBulkSettings(prev => ({ ...prev, notes: e.target.value }))}
                          className="text-sm"
                        />
                      </div>
                      <Button
                        onClick={applyBulkSettings}
                        disabled={selectedDates.length === 0}
                        className="bg-gradient-to-r from-action-500 to-action-600 hover:from-action-600 hover:to-action-700 text-sm"
                        size="sm"
                      >
                        Apply to Selected
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Calendar Grid */}
              <Card className={cn(borders.container, shadows.level1.className)}>
                <CardContent className="p-0">
                  <div className="border rounded-lg overflow-hidden">
                    {/* Header */}
                    <div className="grid grid-cols-7 bg-slate-50 border-b">
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                        <div key={day} className="p-3 text-center text-sm font-medium text-slate-600">
                          {day}
                        </div>
                      ))}
                    </div>

                    {/* Calendar Days */}
                    <div className="grid grid-cols-7">
                      {calendarDays.map((day, index) => (
                        <motion.button
                          key={index}
                          whileHover={{ scale: day.isPast ? 1 : 1.02 }}
                          className={getDateStyles(day)}
                          disabled={day.isPast || !day.isCurrentMonth}
                          onClick={() => handleDateClick(day.dateStr, day.isPast)}
                        >
                          <div className="flex flex-col items-center gap-1">
                            <span>{day.day}</span>
                            {day.isToday && (
                              <div className="w-1 h-1 bg-primary rounded-full"></div>
                            )}
                            {day.availability && (
                              <div className="flex items-center gap-1">
                                {day.availability.isAvailable ? (
                                  <CheckCircle className="h-2.5 w-2.5 text-green-600" />
                                ) : (
                                  <XCircle className="h-2.5 w-2.5 text-red-600" />
                                )}
                                <span className="text-xs">{day.availability.maxBookings}</span>
                              </div>
                            )}
                          </div>
                        </motion.button>
                      ))}
                    </div>
                  </div>

                  {/* Legend */}
                  <div className="p-4 border-t bg-slate-50">
                    <div className="flex items-center gap-6 text-xs text-slate-600">
                      <div className="flex items-center gap-1">
                        <CheckCircle className="h-3 w-3 text-green-600" />
                        Available
                      </div>
                      <div className="flex items-center gap-1">
                        <XCircle className="h-3 w-3 text-red-600" />
                        Unavailable
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3 text-amber-600" />
                        By request
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-3 h-3 bg-primary rounded"></div>
                        Selected
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="templates" className="space-y-6">
              <Card className={cn(borders.container, shadows.level1.className)}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="h-5 w-5 text-amber-600" />
                    Quick Templates for Tour Operators
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {QUICK_TEMPLATES.map((template) => (
                      <Card 
                        key={template.id}
                        className="cursor-pointer transition-all hover:shadow-md border border-slate-200"
                        onClick={() => applyQuickTemplate(template)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-amber-100 border border-amber-200 flex items-center justify-center">
                              {template.icon}
                            </div>
                            <div>
                              <h4 className="font-medium text-slate-900">{template.name}</h4>
                              <p className="text-sm text-slate-600">{template.description}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                  
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-800">
                      <strong>Pro Tip:</strong> Templates apply to the current month. Navigate to different months and apply templates as needed for seasonal planning.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      );
    }

    if (availability.availabilityType === 'by_request') {
      return (
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCurrentStep('settings')}
                className="flex items-center gap-2"
              >
                <ChevronLeft className="w-4 h-4" />
                Back to Mode Selection
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={cn(`bg-${colors.warning.bg} text-${colors.warning.text} border-${colors.warning.border}`)}>
                By Request Only
              </Badge>
            </div>
          </div>

          {/* Request Workflow Settings */}
          <Card className={cn(borders.container, shadows.level1.className)}>
            <CardHeader className={cn(`bg-${colors.neutral[50]}/80 border-b border-${colors.neutral[200]}/50`)}>
              <CardTitle className={cn(typography.h4, `text-${colors.neutral[900]}`)}>
                Request Workflow
              </CardTitle>
            </CardHeader>
            <CardContent className={spacing.cardPadding}>
              <div className="space-y-4">
                <div className={cn(
                  "p-4 rounded-lg border",
                  `bg-${colors.warning.bg}`,
                  `border-${colors.warning.border}`
                )}>
                  <div className="flex items-start gap-3">
                    <MessageSquare className={cn("w-5 h-5 mt-0.5", `text-${colors.warning.text}`)} />
                    <div>
                      <p className={cn(typography.body, "font-medium", `text-${colors.warning.text}`)}>
                        Request-Based Booking
                      </p>
                      <p className={cn(typography.caption, `text-${colors.warning.text}`, "mt-1")}>
                        Customers will submit booking requests that you can approve or decline manually. 
                        This gives you full control over scheduling and allows for custom arrangements.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="text-center py-8">
                  <MessageSquare className={cn("w-16 h-16 mx-auto mb-4", `text-${colors.neutral[400]}`)} />
                  <p className={cn(typography.body, `text-${colors.neutral[600]}`)}>
                    Request workflow configuration will be available in the next update.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return null;
  };

  if (loading) {
    return (
      <Card className={cn(borders.container, shadows.level1.className)}>
        <CardContent className="p-6">
          <div className="flex items-center justify-center h-96">
            <div className="flex flex-col items-center gap-3">
              <div className={cn("w-8 h-8 border-2 border-t-transparent rounded-full animate-spin", `border-${colors.primary.main}`)}></div>
              <p className={cn(typography.body, `text-${colors.neutral[600]}`)}>Loading availability...</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className={cn(borders.container, shadows.level2.className)}>
        <CardHeader className={cn(`bg-${colors.neutral[50]}/80 border-b border-${colors.neutral[200]}/50`)}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={cn(
                "w-12 h-12 rounded-xl flex items-center justify-center",
                `bg-gradient-to-br from-${colors.primary.main} to-${colors.primary.light}`,
                shadows.colored.blue
              )}>
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <div>
                <CardTitle className={cn(typography.h3, `text-${colors.neutral[900]}`)}>
                  Smart Availability Calendar
                </CardTitle>
                <p className={cn(typography.body, `text-${colors.neutral[600]}`, "mt-1")}>
                  Manage when "{adventureName}" is available for booking
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                onClick={saveAvailability} 
                disabled={saving}
                className={cn(
                  `bg-${colors.success.main} hover:bg-${colors.success.hover}`,
                  shadows.simple.md
                )}
              >
                {saving ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Saving...
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Save className="h-4 w-4" />
                    Save Settings
                  </div>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Main Content */}
      <AnimatePresence mode="wait">
        {currentStep === 'settings' ? (
          <motion.div
            key="settings"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.3 }}
          >
            <AvailabilityModeSelector />
          </motion.div>
        ) : (
          <motion.div
            key="configuration"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <ConfigurationInterface />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
} 